/**
 * Shared archive filter, search, and pagination behavior.
 *
 * @param {Object} config Filter config.
 * @param {string} config.archiveSelector Archive root selector.
 * @param {string} config.gridSelector Results container selector.
 * @param {string} config.countSelector Count element selector.
 * @param {string} config.tabSelector Tab selector.
 * @param {string} config.paginationButtonSelector Pagination button selector.
 * @param {string} config.scrollTargetSelector Scroll target selector.
 * @param {string} config.searchParam Search query param name.
 * @param {string} config.errorContext Console error context.
 * @param {string} config.gridWrapperClass Grid wrapper class list.
 * @param {string} config.paginationContainerAttr Pagination container attribute markup.
 * @param {Object} config.defaultStrings Default UI strings.
 * @param {string} [config.stringsGlobal] Window key for localized strings.
 */
export function initArchiveFilter(config) {
	const archive = document.querySelector(config.archiveSelector);

	if (!archive) {
		return;
	}

	const restUrl = archive.dataset.restUrl;
	const grid = archive.querySelector(config.gridSelector);
	const count = archive.querySelector(config.countSelector);
	const searchToggle = archive.querySelector('[data-search-toggle]');
	const searchPanel = archive.querySelector('[data-search-panel]');
	const searchInput = archive.querySelector('[data-search-input]');
	const searchCloseButtons = archive.querySelectorAll('[data-search-close]');
	const searchWrap = archive.querySelector('[data-search-wrap]');
	const tabs = archive.querySelectorAll(config.tabSelector);
	const strings = config.stringsGlobal && window[config.stringsGlobal]?.strings
		? window[config.stringsGlobal].strings
		: config.defaultStrings;

	if (!restUrl || !grid) {
		return;
	}

	let activeCategory = '';
	let activeSearch = '';
	let activeView = '';
	let currentPage = 1;
	let fetchController = null;

	const activeTab = archive.querySelector(`${config.tabSelector}[aria-current="true"]`);
	if (activeTab) {
		activeCategory = activeTab.dataset.category || '';
	}

	if (searchInput) {
		activeSearch = searchInput.value || '';
	}

	// Preserve the current archive view (e.g. all|upcoming|past) across AJAX paginated
	// requests so clicking "Next page" doesn't drop back to the REST endpoint's default.
	const initialParams = new URLSearchParams(window.location.search);
	activeView = initialParams.get('view') || '';

	async function fetchResults() {
		if (fetchController) {
			fetchController.abort();
		}

		fetchController = new AbortController();

		const params = new URLSearchParams();
		if (activeCategory) {
			params.set('category', activeCategory);
		}
		if (activeSearch) {
			params.set('search', activeSearch);
		}
		if (activeView) {
			params.set('view', activeView);
		}
		params.set('page', currentPage);

		grid.style.opacity = '0.5';
		grid.style.pointerEvents = 'none';

		try {
			const response = await fetch(`${restUrl}?${params.toString()}`, { signal: fetchController.signal });
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const data = await response.json();

			if (count) {
				count.textContent = data.count;
			}

			if (data.html) {
				grid.innerHTML = `
					<div class="${config.gridWrapperClass}">
						${data.html}
					</div>
					<div ${config.paginationContainerAttr}>${data.pagination}</div>
				`;
			} else {
				grid.innerHTML = `<p class="type-body-200 text-rmh-gray-300">${strings.noResults}</p>`;
			}

			bindPagination();

			const stateParams = new URLSearchParams();
			if (activeCategory) {
				stateParams.set('category', activeCategory);
			}
			if (activeSearch) {
				stateParams.set(config.searchParam, activeSearch);
			}
			if (activeView) {
				stateParams.set('view', activeView);
			}
			if (currentPage > 1) {
				stateParams.set('paged', currentPage);
			}

			const stateUrl = stateParams.toString()
				? `${window.location.pathname}?${stateParams.toString()}`
				: window.location.pathname;
			window.history.replaceState(null, '', stateUrl);
		} catch (error) {
			if (error.name !== 'AbortError') {
				// eslint-disable-next-line no-console
				console.error(`${config.errorContext}:`, error);
				grid.innerHTML = `<p class="type-body-200 text-rmh-gray-300">${strings.error}</p>`;
			}
		} finally {
			grid.style.opacity = '';
			grid.style.pointerEvents = '';
			fetchController = null;
		}
	}

	function bindPagination() {
		grid.querySelectorAll(config.paginationButtonSelector).forEach((button) => {
			button.addEventListener('click', () => {
				currentPage = parseInt(button.dataset.page, 10) || 1;
				fetchResults();

				const scrollTarget = archive.querySelector(config.scrollTargetSelector);
				if (scrollTarget) {
					scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
		});
	}

	bindPagination();

	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			tabs.forEach((item) => {
				item.classList.remove('btn-solid-red', 'hover:bg-rmh-red');
				item.classList.add('bg-rmh-soft-white', 'border-rmh-gray-100', 'hover:bg-rmh-red', 'hover:text-rmh-soft-white');
				item.removeAttribute('aria-current');
			});

			tab.classList.remove('bg-rmh-soft-white', 'border-rmh-gray-100', 'hover:text-rmh-soft-white');
			tab.classList.add('btn-solid-red', 'hover:bg-rmh-red');
			tab.setAttribute('aria-current', 'true');

			activeCategory = tab.dataset.category || '';
			currentPage = 1;
			fetchResults();
		});
	});

	if (searchToggle && searchPanel && searchInput) {
		const desktopCloseButtons = archive.querySelectorAll('[data-search-close-desktop]');
		const mobileCloseButtons = archive.querySelectorAll('[data-search-close-mobile]');

		function openSearch() {
			searchPanel.classList.remove('hidden');
			searchPanel.classList.add('flex');
			mobileCloseButtons.forEach((button) => {
				button.classList.remove('hidden');
				button.classList.add('inline-flex');
			});
			searchToggle.classList.add('hidden');
			searchToggle.classList.remove('inline-flex');
			if (searchWrap) {
				searchWrap.classList.add('is-open');
			}
			searchInput.focus();
		}

		function closeSearch() {
			searchPanel.classList.add('hidden');
			searchPanel.classList.remove('flex');
			mobileCloseButtons.forEach((button) => {
				button.classList.add('hidden');
				button.classList.remove('inline-flex');
			});
			searchToggle.classList.remove('hidden');
			searchToggle.classList.add('inline-flex');
			if (searchWrap) {
				searchWrap.classList.remove('is-open');
			}

			if (activeSearch) {
				activeSearch = '';
				searchInput.value = '';
				currentPage = 1;
				fetchResults();
			}
		}

		searchToggle.addEventListener('click', openSearch);

		searchCloseButtons.forEach((button) => {
			button.addEventListener('click', closeSearch);
		});

		searchInput.addEventListener('keydown', (event) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				activeSearch = searchInput.value.trim();
				currentPage = 1;
				fetchResults();
			} else if (event.key === 'Escape') {
				closeSearch();
			}
		});

		if (activeSearch) {
			openSearch();
		}
	}
}
